<template>
    <common-modal class="custom-reservation" :showCloseIcon="true" width="base">
        <common-content-spacer height="tiny" @click.stop>
            <p v-show="modalInfo" class="custom-reservation__info">
                {{ modalInfo }}
            </p>
            <img class="custom-reservation__image" :src="calendarImage" :alt="$t('alt.reservation')" />
            <h4 class="custom-reservation__heading">
                <common-info-tip>
                    <p v-for="(item, index) in getCategoriesTitle" :key="index">{{ item }}</p>
                </common-info-tip>
                {{ getTranslation(offer?.titleTextContent) }}
            </h4>
            <h5 v-show="modalSubtitle" :class="subtitleClass">
                {{ modalSubtitle }}
            </h5>
        </common-content-spacer>
        <div v-show="!isConfirmed" @click.stop>
            <form-wrapper @form-submit="checkClient">
                <form-row>
                    <form-cell>
                        <datepicker-input
                            :formData="data"
                            :label="$t('forms.availability.add_availability.from_date.label')"
                            :placeholder="$t('forms.availability.add_availability.from_date.placeholder')"
                            valuePath="date"
                            :prefillValue="getCurrentDay"
                            :lowerLimit="lowerLimit"
                            :isRequired="true"
                            @date-changed="dateChanged"
                        ></datepicker-input>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell width="half">
                        <select-input
                            :formData="data"
                            :label="$t('common.microcopy.hours')"
                            :placeholder="$t('common.microcopy.hours')"
                            :options="hoursOptions"
                            :isRequired="true"
                            valuePath="hours"
                            @change="change"
                        ></select-input>
                    </form-cell>

                    <form-cell width="half">
                        <select-input
                            :formData="data"
                            :isDisabled="!data.hours"
                            :label="$t('common.microcopy.minutes')"
                            :placeholder="$t('common.microcopy.minutes')"
                            :options="minutesOptions"
                            :isRequired="true"
                            valuePath="minutes"
                            @change="change"
                        ></select-input>
                    </form-cell>
                </form-row>
                <div v-if="!calendarEventId" class="custom-reservation--clients">
                    <checkbox-input
                        class="custom-reservation--clients--toggle"
                        :label="secondLabel"
                        :secondLabel="$t('pages.company.company_clients.add_new_client')"
                        type="switch"
                        labelPosition="left"
                        :value="isClientSearchPreview"
                        @change="toggleClientPreview"
                    ></checkbox-input>
                    <common-client-search-autocomplete
                        v-show="!isClientSearchPreview"
                        ref="clientAC"
                        :companyId="offer?.location?.companyServiceProvider?.id"
                        @client-selected="onClientSelected"
                        @client-removed="onClientRemoved"
                        @toggle-client-preview="isClientSearchPreview = !isClientSearchPreview"
                    ></common-client-search-autocomplete>
                    <add-client-form
                        v-show="isClientSearchPreview"
                        ref="clientForm"
                        :companyId="offer?.location?.companyServiceProvider?.id"
                        :showDescription="false"
                        :showSubmitButton="false"
                        @client-added="onClientAdded"
                        @client-already-exist="onClientAlreadyExist"
                    ></add-client-form>
                </div>
                <form-row v-if="!calendarEventId">
                    <form-cell>
                        <textarea-input
                            :placeholder="$t('modals.confirm_reservation_modal.add_note')"
                            type="text"
                            @change="descriptionChange"
                        ></textarea-input>
                    </form-cell>
                </form-row>
                <form-row v-else>
                    <form-cell>
                        <textarea-input
                            :placeholder="$t('modals.confirm_reservation_modal.add_note')"
                            type="text"
                            :prefillVal="moderatorNote"
                            @change="moderatorNoteChange"
                        ></textarea-input>
                    </form-cell>
                </form-row>
                <template #footer>
                    <common-content-spacer height="medium">
                        <div class="custom-reservation--warning">
                            {{ $t('modals.custom_reservation_modal.info') }}
                        </div>
                        <common-buttons align="center">
                            <common-button
                                type="primary"
                                :isProcessing="isLoading"
                                :isSubmittable="true"
                                :isFluid="false"
                                :label="$t('common.microcopy.confirm')"
                            ></common-button>
                        </common-buttons>
                    </common-content-spacer>
                </template>
            </form-wrapper>
        </div>
        <div v-show="isConfirmed">
            <common-button
                type="primary"
                size="large"
                :isProcessing="isLoading"
                :isSubmittable="false"
                :isFluid="true"
                :label="$t('common.microcopy.done')"
                @click="finishReservation()"
            ></common-button>
        </div>
        <common-yes-no-modal
            v-if="isYesNoModalOpen"
            :showWarningIcon="true"
            height="tiny"
            :headingText="$t('pages.company.company_clients.client_not_selected')"
            :noButtonText="$t('common.microcopy.back')"
            :yesButtonText="$t('common.microcopy.confirm')"
            @close="closeYesNoModal"
            @continue="continueReservation"
        ></common-yes-no-modal>
    </common-modal>
</template>

<script>
    import DatepickerInput from 'components/form-inputs/DatepickerInput'
    import FormMixin from 'mixins/FormMixin'
    import TextareaInput from 'components/form-inputs/TextareaInput'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import AddClientForm from 'components/forms/company/AddClientForm'
    import dayjs from 'dayjs'
    import TranslateMixin from 'mixins/TranslateMixin'
    import RoutesEnum from 'enums/routes.enum'
    import availabilityTransformer from 'transformers/availabilityTransformer'
    import CalendarEventAPI from 'api/CalendarEventAPI'
    import AvailabilityEnums from 'enums/availability.enum'
    import CalendarEventEnums from 'enums/calendar_event.enum'
    import { dateFormat } from '~/helpers'

    export default {
        mixins: [FormMixin, TranslateMixin],

        components: { DatepickerInput, TextareaInput, CheckboxInput, AddClientForm },

        props: {
            offer: { type: Object, required: true },
            availabilityOfferId: { type: Number, required: true },
            categories: { type: Array, required: true },
            calendarEventId: { type: [Number, String], default: undefined },
            moderatorNoteProp: { type: String, default: undefined },
        },

        data() {
            return {
                selectedHour: 0,
                selectedMinute: 0,
                isClientSearchPreview: false,
                userId: null,
                isYesNoModalOpen: false,
                reservationCalendarImage: require('../../../assets/images/calendar.png'),
                confirmedCalendarImage: require('../../../assets/images/calendar-approved.png'),
                isConfirmed: false,
                moderatorNote: undefined,
                description: undefined,
            }
        },

        computed: {
            calendarImage() {
                return this.isConfirmed ? this.confirmedCalendarImage : this.reservationCalendarImage
            },

            getCategoriesTitle() {
                return this.categories?.map((item) => {
                    if (this.getTranslation(item?.titleTextContent).length > 20) {
                        return `${this.getTranslation(item?.titleTextContent).substring(0, 20)}...`
                    } else {
                        return this.getTranslation(item?.titleTextContent)
                    }
                })
            },

            getCurrentDay() {
                return dayjs().format('YYYY-MM-DD')
            },

            modalSubtitle() {
                return this.isConfirmed
                    ? this.calendarEventId
                        ? null
                        : this.$t('modals.confirm_reservation_modal.reservation_confirmed_subtitle')
                    : `${this.$auth.getUser().firstName} ${this.$auth.getUser().lastName}`
            },

            subtitleClass() {
                return {
                    'custom-reservation__subtitle': true,
                    'custom-reservation__subtitle--color': !this.isConfirmed,
                }
            },

            modalInfo() {
                return this.isConfirmed
                    ? this.calendarEventId
                        ? null
                        : this.$t('modals.confirm_reservation_modal.reservation_confirmed_info')
                    : this.offer.titleTextContent.originalText
            },

            secondLabel() {
                return `${this.$t('pages.company.company_clients.search_clients')} ${this.$t(
                    'pages.company.company_clients.clients',
                )}`
            },
            lowerLimit() {
                return new Date()
            },

            hoursOptions() {
                const currentHour = dayjs().hour()
                const currentDate = dayjs().format('YYYY-MM-DD')
                const futureMinuteLimit = Math.ceil(dayjs().minute() / 5) * 5 + 15

                return Array.from({ length: 24 }, (_, i) => {
                    const value = i === 0 ? i.toString() : i
                    const label = i < 10 ? `0${i}` : `${i}`
                    if (this.data.date === currentDate && i < currentHour) {
                        return null
                    }
                    if (this.data.date === currentDate && i === currentHour) {
                        const hasValidMinutes = this.minutesOptions?.some(
                            (minuteOption) => Number(minuteOption.value) >= futureMinuteLimit,
                        )
                        if (!hasValidMinutes) {
                            return null
                        }
                    }

                    return { value, label }
                }).filter(Boolean)
            },

            minutesOptions() {
                const currentMinute = dayjs().minute()
                const currentHour = dayjs().hour()
                const currentDate = dayjs().format('YYYY-MM-DD')
                const roundedCurrentMinute = Math.ceil(currentMinute / 5) * 5
                const futureMinuteLimit = roundedCurrentMinute + 15
                return Array.from({ length: 12 }, (_, i) => {
                    const value = i * 5 === 0 ? (i * 5).toString() : i * 5
                    const label = Number(value) < 10 ? `0${Number(value)}` : value
                    if (this.data.date === currentDate && this.data.hours == currentHour && Number(value) < futureMinuteLimit) {
                        return null
                    }
                    return { value, label }
                }).filter(Boolean)
            },
        },

        methods: {
            dateChanged(value) {
                if (value !== undefined) {
                    this.lowerLimit = dateFormat(value)
                    this.setValue('date', value)
                } else {
                    this.lowerLimit = new Date()
                    delete this.data['date']
                }
            },

            formatHour(hour) {
                return hour < 10 ? `0${hour}` : hour
            },
            formatMinute(minute) {
                return minute < 10 ? `0${minute}` : minute
            },

            descriptionChange({ value }) {
                if (value === undefined) {
                    return
                }
                this.description = value
            },

            moderatorNoteChange({ value }) {
                if (value === undefined) {
                    return
                }
                this.moderatorNote = value
            },

            onClientSelected(payload) {
                this.userId = payload
            },

            onClientRemoved() {
                this.userId = undefined
            },

            checkClient() {
                if (!this.userId && this.$refs.clientAC?.searchTerm?.length && !this.isClientSearchPreview) {
                    this.isYesNoModalOpen = true
                    return
                }
                if (this.isClientSearchPreview) {
                    this.$refs.clientForm.submit()
                    return
                }
                this.submit()
            },

            submit() {
                this.isLoading = true

                if (this.calendarEventId) {
                    const request = {
                        date: this.data.date,
                        calendarEventId: Number(this.calendarEventId),
                        availabilityOfferId: this.availabilityOfferId,
                        from: availabilityTransformer.minutesDateToUTCOffset(
                            Number(this.data.hours),
                            Number(this.data.minutes),
                            this.offer.location.timeZone,
                            this.data.date,
                        ),
                        moderatorNote: this.moderatorNote,
                        offerCategoryIds: this.categories.map((item) => item.id),
                        freeSchedule: false,
                        customReservation: true,
                    }
                    CalendarEventAPI.reschedule(request).then(() => {
                        this.isConfirmed = true
                        this.isLoading = false
                    })
                } else {
                    const request = {
                        availabilityOfferId: this.availabilityOfferId,
                        date: this.data.date,
                        description: this.description,
                        freeSchedule: false,
                        from: availabilityTransformer.minutesDateToUTCOffset(
                            Number(this.data.hours),
                            Number(this.data.minutes),
                            this.offer.location.timeZone,
                            this.data.date,
                        ),
                        offerCategoryIds: this.categories.map((item) => item.id),
                        userId: this.userId,
                        customReservation: true,
                    }
                    CalendarEventAPI.createReservation(request)
                        .then((res) => {
                            this.newReservationId = res.id
                            this.isConfirmed = true
                            this.isLoading = false
                        })
                        .catch((error) => {
                            if (error.message === CalendarEventEnums.ERRORS.CALENDAR_EVENT_BLOCKED) {
                                this.$toast.error(this.$t('modals.confirm_reservation_modal.user_blocked'))
                                this.emitEvent({ name: 'reservation-failed' })
                                this.isLoading = false
                                return
                            }

                            let calendarEventError
                            if (error?.error?.invalidFields) {
                                calendarEventError = error?.error?.invalidFields[0]
                            }

                            if (
                                calendarEventError?.message === AvailabilityEnums.ERRORS.UNKOWN_AVAILABILITY_OFFER &&
                                calendarEventError?.field === 'availabilityOfferId'
                            ) {
                                this.$toast.error(this.$t('common.errors.common_error'))
                                this.$router.push({ name: RoutesEnum.INDEX.HOME })
                                return
                            }
                            if (calendarEventError?.field === 'from') {
                                if (calendarEventError?.message.includes(CalendarEventEnums.ERRORS.MINIMUM_DIFFERENCE_IS_)) {
                                    const matchedString = calendarEventError?.message.match(/(\d+)min/)
                                    const minutes = matchedString[1]

                                    this.$toast.error(this.$t(`modals.confirm_reservation_modal.mins_ahead_${minutes}`))
                                } else if (calendarEventError?.message === CalendarEventEnums.ERRORS.UNAVAILABLE_SLOT) {
                                    this.$toast.error(this.$t('pages.calendar.my_calendar.unavailable_slot'))
                                } else {
                                    this.$toast.error(this.$t('common.errors.common_error'))
                                }
                            } else {
                                this.$toast.error(this.$t('common.errors.common_error'))
                            }

                            this.emitEvent({ name: 'reservation-failed' })
                            this.isLoading = false
                        })
                }
                this.isLoading = false
            },

            onClientAdded(payload) {
                this.userId = payload
                this.submit()
            },

            continueReservation(event) {
                this.closeYesNoModal(event.$event)
                this.submit()
            },

            closeYesNoModal(event) {
                if (event) {
                    event.stopPropagation()
                }
                this.isYesNoModalOpen = false
            },

            toggleClientPreview() {
                this.isClientSearchPreview = !this.isClientSearchPreview
                this.$refs.clientAC.removeSelectedClient()
            },

            finishReservation() {
                this.calendarEventId
                    ? this.$router.push({ name: RoutesEnum.RESERVATIONS.SINGLE, params: { id: this.calendarEventId } })
                    : this.$router.push({ name: RoutesEnum.RESERVATIONS.SINGLE, params: { id: this.newReservationId } })
            },
        },

        mounted() {
            if (this.calendarEventId) {
                this.moderatorNote = this.moderatorNoteProp
            }
            this.data.date = dayjs().format('YYYY-MM-DD')
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    .custom-reservation {
        min-height: 300px;

        &__heading {
            font-size: $font-size--heading;
            color: $color--txt-header;
            font-weight: bold;
            text-align: center;
            word-break: break-word;
        }

        &--clients {
            margin: 20px 0;
            padding: 10px;
            box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
            &--toggle {
                margin-bottom: 10px;
            }
        }

        &__info {
            font-size: $font-size--medium;
            color: $color--txt-base-3;
            text-align: center;
            word-break: break-word;
        }

        &__subtitle {
            font-size: $font-size--secondary;
            text-align: center;
            &--color {
                font-size: $font-size--base;
                color: $color--accent-1-lite;
            }
        }

        &--warning {
            text-align: center;
        }

        &__image {
            display: block;
            margin-top: 10px;
            margin-bottom: 20px;
            margin-left: auto;
            margin-right: auto;
            max-width: 40%;
            height: auto;
        }
    }
</style>
